import { TypedStorage } from "@utils/storage";
import { STORAGE_KEYS } from "@constants/storage-keys";
import { DATA_ATTR } from "@constants/data-attributes";
import { isSkininiSettings } from "@validation/skinini.schema";
import { rgbStringToCss, rgbCssToString } from "@utils/color";
import { $, $$ } from "@utils/dom";
import type { SkininiSettings } from "@app-types/skinini.types";

const storage = new TypedStorage<SkininiSettings>(
  STORAGE_KEYS.SKININI,
  isSkininiSettings
);

/**
 * Get all skinini settings from localStorage
 */
export function getSkinini(): SkininiSettings {
  return storage.getOrDefault({});
}

/**
 * Set a single skinini value
 */
export function setSkininiValue(key: string, value: string | number): void {
  const skinini = getSkinini();
  skinini[key] = value;
  storage.set(skinini);
}

/**
 * Remove a single skinini value
 */
export function removeSkininiValue(key: string): void {
  const skinini = getSkinini();
  delete skinini[key];
  storage.set(skinini);
}

/**
 * Clear all skinini settings
 */
export function clearSkinini(): void {
  storage.clear();
}

/**
 * Apply stored skinini values to inputs on page load
 */
function applySavedValues(container: HTMLElement): void {
  const savedValues = getSkinini();

  if (Object.keys(savedValues).length === 0) {
    return; // No saved values, use defaults
  }

  // Apply to text and number inputs
  const textAndNumberInputs = $$<HTMLInputElement>(
    'input[type="text"][name], input[type="number"][name]',
    container
  );
  textAndNumberInputs.forEach((input) => {
    const name = input.name;
    if (name && savedValues[name] !== undefined) {
      input.value = String(savedValues[name]);
    }
  });

  // Apply to color inputs (hidden inputs inside data-colorpicker)
  const colorInputs = $$<HTMLInputElement>(
    '[data-colorpicker] input[type="hidden"][name]',
    container
  );
  colorInputs.forEach((input) => {
    const name = input.name;
    if (name && savedValues[name] !== undefined) {
      const savedValue = savedValues[name];
      // Handle "transparent" value - set empty so colorpicker shows checkerboard
      if (savedValue === "transparent") {
        input.setAttribute("value", "");
      } else if (typeof savedValue === "string") {
        // Convert "rgb(r, g, b)" back to "r,g,b" for the hidden input
        const rgbString = rgbCssToString(savedValue);
        if (rgbString) {
          input.setAttribute("value", rgbString);
        }
      }
    } else if (name && name.startsWith("Combo")) {
      // Combo colors not in localStorage should default to transparent
      input.setAttribute("value", "");
      setSkininiValue(name, "transparent");
    }
  });

  // Apply to checkbox inputs
  const checkboxInputs = $$<HTMLInputElement>(
    'input[type="checkbox"][name]',
    container
  );
  checkboxInputs.forEach((input) => {
    const name = input.name;
    if (name && savedValues[name] !== undefined) {
      input.checked = savedValues[name] === 1;
    }
  });
}

/**
 * Handle input change for text and number inputs
 */
function handleTextNumberChange(
  event: Event,
  updateProjectName: () => void
): void {
  const input = event.target as HTMLInputElement;
  const name = input.name;

  if (!name) return;

  const value =
    input.type === "number" ? parseFloat(input.value) : input.value;
  setSkininiValue(name, value);

  // Update project name in sidebar when Name field changes
  if (name === "Name") {
    updateProjectName();
  }
}

/**
 * Handle change for checkbox inputs
 */
function handleCheckboxChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const name = input.name;

  if (!name) return;

  const value = input.checked ? 1 : 0;
  setSkininiValue(name, value);
}

/**
 * Handle change for color inputs
 */
function handleColorChange(input: HTMLInputElement): void {
  const name = input.name;
  if (!name) return;

  const formattedValue = rgbStringToCss(input.value);
  if (formattedValue) {
    setSkininiValue(name, formattedValue);
  } else {
    setSkininiValue(name, "transparent");
  }
}

/**
 * Initialize skinini functionality
 * Sets up change listeners and loads saved values
 * @param updateProjectName - Callback to update project name display
 */
export function initSkinini(updateProjectName: () => void): void {
  const container = $(DATA_ATTR.SKIN_INI);

  if (!container) return;

  // Apply saved values from localStorage
  applySavedValues(container);

  // Set up listeners for text and number inputs
  const textAndNumberInputs = $$<HTMLInputElement>(
    'input[type="text"][name], input[type="number"][name]',
    container
  );
  textAndNumberInputs.forEach((input) => {
    input.addEventListener("input", (e) =>
      handleTextNumberChange(e, updateProjectName)
    );
  });

  // Set up listeners for checkbox inputs
  const checkboxInputs = $$<HTMLInputElement>(
    'input[type="checkbox"][name]',
    container
  );
  checkboxInputs.forEach((input) => {
    input.addEventListener("change", handleCheckboxChange);
  });

  // Set up listeners for color inputs (hidden inputs)
  // We intercept the value property setter since colorpicker updates
  // the value programmatically via input.value = ...
  const colorInputs = $$<HTMLInputElement>(
    '[data-colorpicker] input[type="hidden"][name]',
    container
  );
  colorInputs.forEach((input) => {
    // Store the original value descriptor
    const descriptor = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      "value"
    );

    if (!descriptor) return;

    // Override the value property to intercept changes
    Object.defineProperty(input, "value", {
      get() {
        return descriptor.get?.call(this) as string;
      },
      set(newValue: string) {
        const oldValue = descriptor.get?.call(this) as string;
        descriptor.set?.call(this, newValue);

        // Only save if value actually changed
        if (oldValue !== newValue) {
          handleColorChange(this as HTMLInputElement);
        }
      },
      configurable: true,
    });

    // Also listen for manual input/change events (fallback)
    input.addEventListener("input", () => handleColorChange(input));
    input.addEventListener("change", () => handleColorChange(input));
  });
}
